import { useState } from 'react';
import MultiSelect from '../ContactCenter/MultiSelect';

// Local, client-side faceted filtering for a table whose rows are already on
// the page — e.g. the "all cost centers" admin view of Vacancies/New Hires/
// Mechanic Development, where a flat list spanning every facility is long
// enough that a reader wants to narrow it by Job Profile, Manager, Cost
// Center, etc. without leaving this view or changing the page's cost-center
// focus (that's a different, bigger operation — see StaffingTracker.jsx).
//
// facets: [{ key, label, get: (row) => string | null }]
export function useFacetFilters(rows, facets) {
  const [selected, setSelected] = useState(() => Object.fromEntries(facets.map((f) => [f.key, []])));
  const setFacet = (key, values) => setSelected((s) => ({ ...s, [key]: values }));
  // A selection can outlive the rows it was made against (the page's cost
  // center changed underneath it). A stale value is no longer in FacetFilterBar's
  // option list, so the reader could neither see nor uncheck it while it
  // silently emptied the table -- only values that still exist in `rows` count,
  // and `selected` is trimmed to match so the bar and the table agree.
  const available = Object.fromEntries(facets.map((f) => [f.key, new Set(rows.map((row) => f.get(row) || '—'))]));
  const effective = Object.fromEntries(facets.map((f) => [f.key, (selected[f.key] || []).filter((v) => available[f.key].has(v))]));
  const filtered = rows.filter((row) => facets.every((f) => {
    const sel = effective[f.key];
    if (!sel.length) return true;
    return sel.includes(f.get(row) || '—');
  }));
  return { filtered, selected: effective, setFacet };
}

export function FacetFilterBar({ rows, facets, selected, setFacet }) {
  if (!rows.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '10px 16px', borderBottom: '1px solid #E5E7EB' }}>
      {facets.map((f) => {
        const options = [...new Set(rows.map((r) => f.get(r) || '—'))].sort((a, b) => a.localeCompare(b));
        return (
          <MultiSelect
            key={f.key} label={f.label} options={options} selected={selected[f.key] || []}
            onChange={(v) => setFacet(f.key, v)} allLabel="All" noun="selected" emptyMeansAll align="left"
          />
        );
      })}
    </div>
  );
}
